import type { ReactNode } from 'react';
import { Icon } from './icons.js';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/**
 * S M T W T F S, any number of days. At least one stays on — turning a
 * reminder off entirely is its toggle's job.
 */
export function DayPicker({
  days,
  onChange,
  label,
}: {
  days: readonly number[];
  onChange(days: number[]): void;
  label: string;
}): ReactNode {
  return (
    <div className="flex justify-between gap-1" role="group" aria-label={label}>
      {DAY_NAMES.map((name, day) => {
        const on = days.includes(day);
        return (
          <button
            key={name}
            type="button"
            aria-label={name}
            aria-pressed={on}
            disabled={on && days.length === 1}
            onClick={() =>
              onChange(on ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b))
            }
            className={`grid size-9 place-items-center rounded-full text-sm font-semibold transition ${
              on ? 'bg-ink text-white' : 'border border-line bg-white text-ink-soft'
            }`}
          >
            {name.charAt(0)}
          </button>
        );
      })}
    </div>
  );
}

export function formatHour(hour: number): string {
  return new Date(2000, 0, 1, hour).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * A time field that opens the platform's own picker — the wheel on iOS, the
 * list dialog on Android — through a native <select> laid over it. Whole
 * hours only: the reminder job runs once an hour, so a minute would be a
 * promise it cannot keep.
 */
export function HourPicker({
  hour,
  onChange,
  label,
  from = 5,
  to = 23,
}: {
  hour: number;
  onChange(hour: number): void;
  label: string;
  from?: number;
  to?: number;
}): ReactNode {
  const hours = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  return (
    <label className="relative flex items-center justify-between rounded-xl border border-line bg-white px-3.5 py-2.5">
      <span className="flex items-center gap-2 text-sm text-ink-soft">
        <ClockIcon />
        {label}
      </span>
      <span className="flex items-center gap-1 text-base font-semibold text-ink tabular-nums">
        {formatHour(hour)}
        <Icon name="back" size={14} className="-rotate-90 text-muted" />
      </span>
      <select
        aria-label={label}
        value={hour}
        onChange={(e) => onChange(Number(e.target.value))}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0"
      >
        {hours.map((h) => (
          <option key={h} value={h}>
            {formatHour(h)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function formatMinute(minute: number): string {
  return new Date(2000, 0, 1, Math.floor(minute / 60), minute % 60).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * HourPicker with minutes, for work hours (half-hour steps) and fixed
 * reminders (the scheduler's ten-minute steps).
 */
export function TimePicker({
  minute,
  onChange,
  label,
  step = 30,
  from = 5 * 60,
  to = 23 * 60 + 30,
}: {
  minute: number;
  onChange(minute: number): void;
  label: string;
  step?: number;
  from?: number;
  to?: number;
}): ReactNode {
  const options = Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, i) => from + i * step);
  if (!options.includes(minute)) options.push(minute);
  options.sort((a, b) => a - b);
  return (
    <label className="relative flex items-center justify-between rounded-xl border border-line bg-white px-3.5 py-2.5">
      <span className="flex items-center gap-2 text-sm text-ink-soft">
        <ClockIcon />
        {label}
      </span>
      <span className="flex items-center gap-1 text-base font-semibold text-ink tabular-nums">
        {formatMinute(minute)}
        <Icon name="back" size={14} className="-rotate-90 text-muted" />
      </span>
      <select
        aria-label={label}
        value={minute}
        onChange={(e) => onChange(Number(e.target.value))}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0"
      >
        {options.map((m) => (
          <option key={m} value={m}>
            {formatMinute(m)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ClockIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}
